import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useLanguage } from '../../hooks/useLanguage';
import { getTranslation } from '../../translations/presentation';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideInFromTop = keyframes`
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const scanLine = keyframes`
  0% {
    transform: translateY(-100%);
  }
  50% {
    transform: translateY(100%);
  }
  100% {
    transform: translateY(-100%);
  }
`;

const glowPulse = keyframes`
  0% {
    box-shadow: 0 0 10px rgba(255, 0, 100, 0.3);
  }
  50% {
    box-shadow: 0 0 30px rgba(255, 0, 100, 0.8);
  }
  100% {
    box-shadow: 0 0 10px rgba(255, 0, 100, 0.3);
  }
`;

const floatingParticles = keyframes`
  0% {
    transform: translateY(0) translateX(0) rotate(0);
  }
  25% {
    transform: translateY(-10px) translateX(5px) rotate(5deg);
  }
  50% {
    transform: translateY(0) translateX(10px) rotate(0);
  }
  75% {
    transform: translateY(10px) translateX(5px) rotate(-5deg);
  }
  100% {
    transform: translateY(0) translateX(0) rotate(0);
  }
`;

const textGlowPulse = keyframes`
  0% {
    text-shadow: 0 0 10px rgba(255, 0, 100, 0.5), 0 0 20px rgba(255, 0, 100, 0.3);
  }
  50% {
    text-shadow: 0 0 20px rgba(255, 0, 100, 0.8), 0 0 30px rgba(255, 0, 100, 0.6), 0 0 40px rgba(255, 0, 100, 0.4);
  }
  100% {
    text-shadow: 0 0 10px rgba(255, 0, 100, 0.5), 0 0 20px rgba(255, 0, 100, 0.3);
  }
`;

const SlideWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  padding: 20px;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  background: transparent;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      linear-gradient(45deg, rgba(0, 0, 0, 0.7), rgba(20, 10, 20, 0.7)),
      repeating-linear-gradient(
        0deg,
        transparent,
        transparent 2px,
        rgba(255, 0, 100, 0.05) 2px,
        rgba(255, 0, 100, 0.05) 4px
      );
    z-index: -1;
    opacity: 0.8;
  }
  
  &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 200%;
    background: linear-gradient(
      to bottom,
      transparent 0%,
      rgba(255, 0, 100, 0.05) 50%,
      transparent 100%
    );
    animation: ${scanLine} 8s linear infinite;
    z-index: -1;
    pointer-events: none;
  }
`;

const SlideTitle = styled.h2`
  font-family: 'Orbitron', sans-serif;
  color: #FF0064;
  margin-bottom: 30px;
  text-align: center;
  letter-spacing: 2px;
  font-size: 1.8rem;
  text-transform: uppercase;
  position: absolute;
  top: 30px;
  left: 0;
  right: 0;
  text-shadow: 0 0 10px rgba(255, 0, 100, 0.5);
  background: transparent;
  
  &::before {
    content: '//';
    color: rgba(255, 0, 100, 0.5);
    margin-right: 10px;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 150px;
    height: 2px;
    background: linear-gradient(90deg, transparent, rgba(255, 0, 100, 0.8), transparent);
  }

  opacity: 0;
  animation: ${slideInFromTop} 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
  animation-delay: 0.2s;
`;

const ContentPreview = styled.div`
  max-width: 80%;
  width: 80%;
  margin: 0 auto;
  padding: 40px;
  border: 2px solid rgba(255, 0, 100, 0.4);
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.3);
  box-shadow: 0 0 30px rgba(255, 0, 100, 0.3);
  backdrop-filter: blur(10px);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, transparent, rgba(255, 0, 100, 0.8), transparent);
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, transparent, rgba(255, 0, 100, 0.8), transparent);
  }
  
  opacity: 0;
  animation: ${scaleIn} 1s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  animation-delay: 0.5s;
`;


const OfferContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
`;

const OfferTitle = styled.h3`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.8rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  text-align: center;
  margin-bottom: 30px;
  letter-spacing: 2px;
  text-transform: uppercase;
`;

const PriceContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  margin-bottom: 40px;
  flex-wrap: wrap;
`;

const StrikethroughPrice = styled.div`
  font-family: 'Orbitron', sans-serif;
  font-size: 2.5rem;
  color: rgba(255, 255, 255, 0.3);
  text-decoration: line-through;
  text-decoration-thickness: 2px;
`;

const ForText = styled.span`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.5rem;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 300;
`;

const SpecialPrice = styled.h3`
  font-family: 'Orbitron', sans-serif;
  font-size: 4.5rem;
  font-weight: 700;
  color: #FF0064;
  text-shadow: 0 0 30px rgba(255, 0, 100, 0.8), 0 0 60px rgba(255, 0, 100, 0.4);
  animation: ${textGlowPulse} 2s infinite;
  line-height: 1;
`;


const Benefits = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 15px;
  align-items: center;
`;

const BenefitItem = styled.li`
  font-family: 'Orbitron', sans-serif;
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.2rem;
  text-align: center;
  position: relative;
  padding-left: 20px;
  
  &::before {
    content: '*';
    position: absolute;
    left: 0;
    color: #FF0064;
    font-weight: 700;
  }
`;


const ParticleContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  pointer-events: none;
  z-index: -1;
`;

const Particle = styled.div`
  position: absolute;
  width: ${props => props.size || '4px'};
  height: ${props => props.size || '4px'};
  background: ${props => props.color || 'rgba(255, 0, 100, 0.5)'};
  border-radius: 50%;
  top: ${props => props.top};
  left: ${props => props.left};
  opacity: ${props => props.opacity || 0.5};
  animation: ${floatingParticles} ${props => props.duration || '8s'} infinite;
  animation-delay: ${props => props.delay || '0s'};
  box-shadow: 0 0 5px ${props => props.color || 'rgba(255, 0, 100, 0.5)'};
`;

const PresentationSlide22B = () => {
  const { lang } = useLanguage();
  const t = getTranslation('slide22b', lang);

  const particles = [
    { size: '5px', color: 'rgba(255, 0, 100, 0.7)', top: '15%', left: '10%', duration: '9s', delay: '0s', opacity: 0.7 },
    { size: '3px', color: 'rgba(255, 50, 50, 0.6)', top: '25%', left: '20%', duration: '10s', delay: '0.5s', opacity: 0.6 },
    { size: '6px', color: 'rgba(255, 0, 100, 0.5)', top: '60%', left: '85%', duration: '12s', delay: '1s', opacity: 0.5 },
    { size: '4px', color: 'rgba(255, 215, 0, 0.7)', top: '80%', left: '30%', duration: '11s', delay: '1.5s', opacity: 0.7 },
    { size: '7px', color: 'rgba(255, 0, 100, 0.6)', top: '40%', left: '70%', duration: '9s', delay: '2s', opacity: 0.6 },
  ];

  return (
    <SlideWrapper>
      <ParticleContainer>
        {particles.map((particle, index) => (
          <Particle 
            key={index}
            size={particle.size}
            color={particle.color}
            top={particle.top}
            left={particle.left}
            duration={particle.duration}
            delay={particle.delay}
            opacity={particle.opacity}
          />
        ))}
      </ParticleContainer>
      
      <SlideTitle>{t.title}</SlideTitle>
      
      <ContentPreview>
        <OfferContainer>
          <OfferTitle>{t.offerTitle}</OfferTitle>
          
          <PriceContainer>
            <StrikethroughPrice>
              {t.currency}{t.oldPrice}
            </StrikethroughPrice>
            <ForText>{t.forText}</ForText>
            <SpecialPrice>
              {t.currency}{t.specialPrice}
            </SpecialPrice>
          </PriceContainer>
          
          <Benefits>
            <BenefitItem>{t.benefits.weeks}</BenefitItem>
            <BenefitItem>{t.benefits.ads}</BenefitItem>
          </Benefits>
        </OfferContainer>
      </ContentPreview>
    </SlideWrapper>
  );
};

export default PresentationSlide22B;
